import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Chip,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Hub as IntegrationIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
  CheckCircle as ConnectedIcon,
  Error as DisconnectedIcon,
  Warning as WarningIcon,
  People as CRMIcon,
  Inventory as SupplyChainIcon,
  CloudSync as APIIcon,
  Webhook as WebhookIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';

import CRMIntegrations from './CRMIntegrations';
import SupplyChainIntegrations from './SupplyChainIntegrations';
import APIManagement from './APIManagement';
import WebhookManagement from './WebhookManagement';

// Types
interface Integration {
  id: string;
  name: string;
  category: 'CRM' | 'SUPPLY_CHAIN' | 'ACCOUNTING' | 'ECOMMERCE' | 'MARKETING';
  provider: string;
  status: 'CONNECTED' | 'DISCONNECTED' | 'PENDING' | 'ERROR';
  last_sync: string;
  sync_frequency: 'REAL_TIME' | 'HOURLY' | 'DAILY' | 'WEEKLY';
  data_flow: 'BIDIRECTIONAL' | 'INBOUND' | 'OUTBOUND';
  logo_url?: string;
  description: string;
  features: string[];
}

interface IntegrationStats {
  total_integrations: number;
  active_integrations: number;
  sync_errors: number;
  data_synced_today: number;
  last_sync: string;
}

const IntegrationManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [integrationStats, setIntegrationStats] = useState<IntegrationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Setup wizard state
  const [setupStep, setSetupStep] = useState<'select' | 'configure'>('select');
  const [selectedProvider, setSelectedProvider] = useState<{ name: string; category: string; logo: string; description: string } | null>(null);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [connecting, setConnecting] = useState(false);

  const integrationTabs = [
    {
      label: 'CRM Systems',
      icon: <CRMIcon />,
      component: CRMIntegrations,
      description: 'Customer relationship management integrations'
    },
    {
      label: 'Supply Chain',
      icon: <SupplyChainIcon />,
      component: SupplyChainIntegrations,
      description: 'Inventory and supplier management systems'
    },
    {
      label: 'API Management',
      icon: <APIIcon />,
      component: APIManagement,
      description: 'REST API endpoints and authentication'
    },
    {
      label: 'Webhooks',
      icon: <WebhookIcon />,
      component: WebhookManagement,
      description: 'Real-time event notifications and triggers'
    }
  ];

  // Popular integration providers
  const availableIntegrations = [
    // CRM Systems
    { name: 'Salesforce', category: 'CRM', logo: '☁️', description: 'World\'s #1 CRM platform' },
    { name: 'HubSpot', category: 'CRM', logo: '🧡', description: 'Inbound marketing and sales' },
    { name: 'Pipedrive', category: 'CRM', logo: '🔵', description: 'Sales-focused CRM' },
    { name: 'Zoho CRM', category: 'CRM', logo: '🟠', description: 'Complete business suite' },

    // Supply Chain
    { name: 'TradeGecko', category: 'SUPPLY_CHAIN', logo: '🦎', description: 'Inventory management' },
    { name: 'Cin7', category: 'SUPPLY_CHAIN', logo: '📦', description: 'Inventory and order management' },
    { name: 'Unleashed', category: 'SUPPLY_CHAIN', logo: '🚀', description: 'Cloud inventory software' },
    { name: 'inFlow', category: 'SUPPLY_CHAIN', logo: '📊', description: 'Inventory management system' },

    // E-commerce
    { name: 'Shopify', category: 'ECOMMERCE', logo: '🛍️', description: 'E-commerce platform' },
    { name: 'WooCommerce', category: 'ECOMMERCE', logo: '🛒', description: 'WordPress e-commerce' },
    { name: 'Magento', category: 'ECOMMERCE', logo: '🏪', description: 'Enterprise e-commerce' },

    // Accounting
    { name: 'Xero', category: 'ACCOUNTING', logo: '💙', description: 'Cloud accounting software' },
    { name: 'QuickBooks', category: 'ACCOUNTING', logo: '💚', description: 'Small business accounting' },
    { name: 'Sage', category: 'ACCOUNTING', logo: '🟢', description: 'Business management software' }
  ];

  useEffect(() => {
    fetchIntegrationData();
  }, []);

  const fetchIntegrationData = async () => {
    try {
      setLoading(true);

      // Mock data for demonstration
      const mockIntegrations: Integration[] = [
        {
          id: '1',
          name: 'Salesforce CRM',
          category: 'CRM',
          provider: 'Salesforce',
          status: 'CONNECTED',
          last_sync: '2024-09-15T10:30:00Z',
          sync_frequency: 'HOURLY',
          data_flow: 'BIDIRECTIONAL',
          description: 'Customer data synchronization with Salesforce',
          features: ['Contact sync', 'Lead management', 'Opportunity tracking']
        },
        {
          id: '2',
          name: 'TradeGecko Inventory',
          category: 'SUPPLY_CHAIN',
          provider: 'TradeGecko',
          status: 'CONNECTED',
          last_sync: '2024-09-15T09:15:00Z',
          sync_frequency: 'DAILY',
          data_flow: 'BIDIRECTIONAL',
          description: 'Inventory and order management integration',
          features: ['Stock levels', 'Purchase orders', 'Supplier management']
        },
        {
          id: '3',
          name: 'Shopify Store',
          category: 'ECOMMERCE',
          provider: 'Shopify',
          status: 'PENDING',
          last_sync: '2024-09-14T18:00:00Z',
          sync_frequency: 'REAL_TIME',
          data_flow: 'INBOUND',
          description: 'E-commerce sales and customer data',
          features: ['Order sync', 'Customer import', 'Product catalog']
        }
      ];

      const mockStats: IntegrationStats = {
        total_integrations: 3,
        active_integrations: 2,
        sync_errors: 0,
        data_synced_today: 1247,
        last_sync: '2024-09-15T10:30:00Z'
      };

      setIntegrations(mockIntegrations);
      setIntegrationStats(mockStats);
    } catch (error) {
      console.error('Error fetching integration data:', error);
      setSnackbar({ open: true, message: 'Failed to load integration data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleIntegration = async (integrationId: string, enabled: boolean) => {
    try {
      await apiClient.post(`/integrations/${integrationId}/toggle`, { enabled });
      setSnackbar({
        open: true,
        message: `Integration ${enabled ? 'enabled' : 'disabled'} successfully!`,
        severity: 'success'
      });
      fetchIntegrationData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update integration', severity: 'error' });
    }
  };

  const handleSyncAll = async () => {
    try {
      await apiClient.post('/integrations/sync-all');
      setSnackbar({ open: true, message: 'All integrations synced successfully!', severity: 'success' });
      fetchIntegrationData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to sync integrations', severity: 'error' });
    }
  };

  const handleSelectProvider = (provider: typeof availableIntegrations[0]) => {
    setSelectedProvider(provider);
    setCredentials({});
    setSetupStep('configure');
  };

  const handleConnectIntegration = async () => {
    if (!selectedProvider) return;
    setConnecting(true);
    try {
      const response = await apiClient.post('/integrations/connect', {
        provider: selectedProvider.name,
        category: selectedProvider.category,
        name: credentials.name || `${selectedProvider.name} Integration`,
        credentials,
      });

      if (response.data.success) {
        // Add the new integration to local state
        if (response.data.integration) {
          setIntegrations(prev => [...prev, response.data.integration]);
        }

        // Handle Xero OAuth redirect
        if (response.data.authorization_url) {
          window.open(response.data.authorization_url, '_blank', 'width=600,height=700');
        }

        setSnackbar({
          open: true,
          message: response.data.message || `Successfully connected ${selectedProvider.name}`,
          severity: 'success'
        });
        setShowAddDialog(false);
        setSetupStep('select');
        setSelectedProvider(null);
        setCredentials({});
        fetchIntegrationData();
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || `Failed to connect ${selectedProvider.name}`,
        severity: 'error'
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
    setSetupStep('select');
    setSelectedProvider(null);
    setCredentials({});
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONNECTED': return 'success';
      case 'DISCONNECTED': return 'error';
      case 'PENDING': return 'warning';
      case 'ERROR': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CONNECTED': return <ConnectedIcon />;
      case 'DISCONNECTED': return <DisconnectedIcon />;
      case 'PENDING': return <WarningIcon />;
      case 'ERROR': return <DisconnectedIcon />;
      default: return <WarningIcon />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'CRM': return 'primary';
      case 'SUPPLY_CHAIN': return 'secondary';
      case 'ECOMMERCE': return 'success';
      case 'ACCOUNTING': return 'info';
      case 'MARKETING': return 'warning';
      default: return 'default';
    }
  };

  const ActiveTabComponent = integrationTabs[activeTab].component;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Integration Management</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Integration Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Connect your business systems for seamless data flow
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
          >
            Add Integration
          </Button>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={handleSyncAll}
          >
            Sync All
          </Button>
        </Box>
      </Box>

      {/* Integration Statistics */}
      {integrationStats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Integration Overview
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={2}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Integrations</Typography>
                  <Typography variant="h6">{integrationStats.total_integrations}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Active</Typography>
                  <Typography variant="h6" color="success.main">
                    {integrationStats.active_integrations}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Sync Errors</Typography>
                  <Typography variant="h6" color={integrationStats.sync_errors > 0 ? 'error.main' : 'success.main'}>
                    {integrationStats.sync_errors}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Data Synced Today</Typography>
                  <Typography variant="h6">{integrationStats.data_synced_today.toLocaleString()} records</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Last Sync</Typography>
                  <Typography variant="body2">
                    {new Date(integrationStats.last_sync).toLocaleString('en-GB')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Active Integrations */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Active Integrations ({integrations.length})
          </Typography>

          <List>
            {integrations.map((integration) => (
              <ListItem key={integration.id}>
                <ListItemAvatar>
                  <Avatar>
                    {integration.provider.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight="medium">
                        {integration.name}
                      </Typography>
                      <Chip
                        label={integration.category.replace('_', ' ')}
                        color={getCategoryColor(integration.category) as any}
                        size="small"
                      />
                      <Chip
                        icon={getStatusIcon(integration.status)}
                        label={integration.status}
                        color={getStatusColor(integration.status) as any}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <span style={{ display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
                        {integration.description}
                      </span>
                      <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                        Last sync: {new Date(integration.last_sync).toLocaleString('en-GB')} •
                        Frequency: {integration.sync_frequency.replace('_', ' ').toLowerCase()} •
                        Flow: {integration.data_flow.toLowerCase()}
                      </span>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      checked={integration.status === 'CONNECTED'}
                      onChange={(e) => handleToggleIntegration(integration.id, e.target.checked)}
                    />
                    <IconButton size="small">
                      <SettingsIcon />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Integration Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            {integrationTabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                iconPosition="start"
                sx={{ minHeight: 72 }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Description */}
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="textSecondary">
            {integrationTabs[activeTab].description}
          </Typography>
        </Box>

        {/* Active Tab Content */}
        <CardContent>
          <ActiveTabComponent
            integrations={integrations}
            onRefresh={fetchIntegrationData}
            loading={loading}
          />
        </CardContent>
      </Card>

      {/* Add Integration Dialog — Two-step wizard */}
      <Dialog open={showAddDialog} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <IntegrationIcon />
            {setupStep === 'select' ? 'Add New Integration' : `Connect ${selectedProvider?.name}`}
          </Box>
        </DialogTitle>
        <DialogContent>
          {setupStep === 'select' && (
            <>
              <Typography variant="h6" gutterBottom>
                Popular Integrations
              </Typography>
              <Grid container spacing={2}>
                {availableIntegrations.map((integration, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => handleSelectProvider(integration)}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="h4">
                            {integration.logo}
                          </Typography>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {integration.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {integration.description}
                            </Typography>
                            <Chip
                              label={integration.category.replace('_', ' ')}
                              color={getCategoryColor(integration.category) as any}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {setupStep === 'configure' && selectedProvider && (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Typography variant="h4">{selectedProvider.logo}</Typography>
                <Box>
                  <Typography variant="h6">{selectedProvider.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedProvider.description}
                  </Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Connection Name"
                placeholder={`My ${selectedProvider.name} Integration`}
                sx={{ mb: 2 }}
                value={credentials.name || ''}
                onChange={(e) => setCredentials({ ...credentials, name: e.target.value })}
              />

              {/* CRM & Supply Chain — API Key + optional instance URL */}
              {(selectedProvider.category === 'CRM' || selectedProvider.category === 'SUPPLY_CHAIN') && (
                <>
                  <TextField
                    fullWidth
                    label="API Key"
                    type="password"
                    placeholder={`Your ${selectedProvider.name} API key`}
                    sx={{ mb: 2 }}
                    required
                    value={credentials.api_key || ''}
                    onChange={(e) => setCredentials({ ...credentials, api_key: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Instance URL (optional)"
                    placeholder={`https://your-instance.${selectedProvider.name.toLowerCase()}.com`}
                    sx={{ mb: 2 }}
                    value={credentials.instance_url || ''}
                    onChange={(e) => setCredentials({ ...credentials, instance_url: e.target.value })}
                  />
                </>
              )}

              {/* E-commerce — Store URL + API Key */}
              {selectedProvider.category === 'ECOMMERCE' && (
                <>
                  <TextField
                    fullWidth
                    label="Store URL"
                    placeholder="https://your-store.myshopify.com"
                    sx={{ mb: 2 }}
                    required
                    value={credentials.store_url || ''}
                    onChange={(e) => setCredentials({ ...credentials, store_url: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="API Key"
                    type="password"
                    placeholder={`Your ${selectedProvider.name} API key`}
                    sx={{ mb: 2 }}
                    required
                    value={credentials.api_key || ''}
                    onChange={(e) => setCredentials({ ...credentials, api_key: e.target.value })}
                  />
                </>
              )}

              {/* Accounting — OAuth info */}
              {selectedProvider.category === 'ACCOUNTING' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>{selectedProvider.name}</strong> uses OAuth for authentication.
                  Clicking "Connect" will open an authorization window where you can sign in
                  and grant access to your {selectedProvider.name} account.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {setupStep === 'configure' && (
            <Button onClick={() => { setSetupStep('select'); setSelectedProvider(null); setCredentials({}); }}>
              Back
            </Button>
          )}
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          {setupStep === 'configure' && (
            <Button
              variant="contained"
              onClick={handleConnectIntegration}
              disabled={connecting}
            >
              {connecting ? 'Connecting...' : 'Connect'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntegrationManagement;
